// apps/widget/src/App.tsx
//
// Boots the widget: read the key from the URL, fetch the tenant config, apply
// their theme, set the locale, open the postMessage bridge.
//
// Everything below renders only once config has arrived — a flash of unbranded
// UI inside a clinic's own site is worse than a moment of nothing.

import { useEffect, useRef, useState } from 'react';
import { api, setKey, WidgetApiError, type WidgetConfig } from './lib/api';
import { applyTheme } from './lib/theme';
import { detectLocale, onLocaleChange, setLocale } from './lib/i18n';
import { configureBridge, listen, reportHeight, send } from './lib/postmessage';
import { isPreviewFrame, listenForPreview, mergeDraft } from './lib/preview';
import { Router } from './router';
import { ErrorScreen } from './screens/error.screen';
import { ScanProvider } from './state';

export function App() {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const shell = useRef<HTMLDivElement>(null);
  // The config as loaded from the API, so each preview draft is laid over the
  // saved settings rather than over the previous draft.
  const saved = useRef<WidgetConfig | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // /w/<key> for the widget; the phone's /m/<sessionId> carries it as ?key=,
    // because that path's second segment is the session, not a key.
    const segments = location.pathname.split('/').filter(Boolean);
    const key = (segments[0] === 'w' ? segments[1] : undefined) ?? params.get('key') ?? '';
    setKey(key);

    void (async () => {
      try {
        const loaded = await api.config();

        applyTheme(loaded);
        configureBridge(loaded.urls.allowedOrigins);

        const locale = params.get('locale') ?? detectLocale(loaded.locales, loaded.defaultLocale);
        await setLocale(locale as never);

        saved.current = loaded;
        setConfig(loaded);
      } catch (e) {
        // No clinic config to choose from: the visitor's language, so the
        // error reads in words rather than as its translation keys.
        await setLocale((params.get('locale') ?? navigator.language.slice(0, 2)) as never);
        setError(
          e instanceof WidgetApiError
            ? e.error
            : { code: 'UNKNOWN', message: 'Could not load the scanner.' },
        );
      }
    })();
  }, []);

  useEffect(() => {
    const stop = listen({
      onClose: () => send('gs:close'),
      onSetLocale: (locale) => void setLocale(locale as never),
    });
    return stop;
  }, []);

  // Every screen reads its words while rendering, so a language change is a
  // re-render — not a remount, which would throw away a scan in progress.
  const [, setLanguageTick] = useState(0);
  useEffect(() => {
    const stop = onLocaleChange(() => setLanguageTick((n) => n + 1));
    return () => {
      stop();
    };
  }, []);

  // The dashboard's live preview: unsaved settings restyle this frame as the
  // clinic edits them. Inert anywhere else.
  const loaded = config !== null;
  useEffect(() => {
    if (!loaded || !saved.current || !isPreviewFrame()) return;
    const base = saved.current;
    return listenForPreview(base.urls.previewOrigins ?? [], (draft) => {
      const next = mergeDraft(base, draft);
      applyTheme(next);
      setConfig(next);
    });
  }, [loaded]);

  // Inline embeds size themselves to content; the host needs to be told.
  useEffect(() => {
    if (!shell.current || !config) return;
    return reportHeight(shell.current);
  }, [config]);

  if (error) {
    return (
      <div className="gs-shell" ref={shell}>
        <ErrorScreen code={error.code} message={error.message} />
      </div>
    );
  }

  if (!config) {
    // Built from the real welcome layout rather than two stacked bars: the
    // opening screen is a two-column split on a wide dialog, and a skeleton
    // shaped like the single-column one made the dialog visibly snap when the
    // config arrived. `.gs-shell:has(.gs-welcome)` drives the split, so reusing
    // those classes means the placeholder cannot drift from the real thing.
    return (
      <div className="gs-shell" ref={shell}>
        <div className="gs-welcome" aria-busy="true">
          <div className="gs-welcome__copy">
            <div className="gs-skeleton" style={{ height: 34, width: '72%' }} />
            <div className="gs-skeleton" style={{ height: 16, width: '92%' }} />
            <div className="gs-skeleton" style={{ height: 16, width: '58%' }} />
            <div className="gs-skeleton" style={{ height: 44, width: 190, borderRadius: 12 }} />
          </div>
          <div className="gs-welcome__visual" />
        </div>
      </div>
    );
  }

  return (
    <div className="gs-shell" ref={shell}>
      <ScanProvider config={config}>
        <Router config={config} />
      </ScanProvider>
    </div>
  );
}
